import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { businesses as bizApi, events as evtApi, misc } from '../services/api';
import { Award, Gavel, Ticket, Calendar, TrendingUp, Trophy, ChevronRight, Sparkles, Heart, Users } from 'lucide-react';
import FundraiserThermometer from '../components/FundraiserThermometer';
import ActivityTicker from '../components/ActivityTicker';
import PBBLoyaltyCard from '../components/PBBLoyaltyCard';
import AppImage from '../components/AppImage';
import BusinessLogoAvatar from '../components/BusinessLogoAvatar';

export default function Dashboard() {
  const { user } = useAuth();
  const [biz, setBiz] = useState([]);
  const [ev, setEv] = useState([]);
  const [lb, setLb] = useState([]);
  const [funds, setFunds] = useState({ community: 0, youth: 0, goal: 500000 });

  useEffect(() => {
    bizApi.list().then(r => setBiz(r.slice(0, 3))).catch(() => {});
    evtApi.list().then(r => setEv(r.slice(0, 3))).catch(() => {});
    misc.leaderboard().then(r => setLb(r.slice(0, 5))).catch(() => {});
    misc.funds().then(setFunds).catch(() => {});
  }, []);

  const totalFund = (funds.community || 0) + (funds.youth || 0);
  const pct = Math.min(100, (totalFund / (funds.goal || 1)) * 100);

  return (
    <div className="space-y-8">
      <div className="pbb-gradient-hero rounded-2xl p-6 lg:p-8 border border-[#122b1c]">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-600/20 text-green-400 text-xs mb-3">
              <Sparkles size={12} /> Welcome back, {user?.name?.split(' ')[0] || 'friend'}
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold leading-tight">Support Black-Owned. <span className="text-green-400">Earn Rewards.</span></h1>
            <p className="text-gray-300 mt-2 max-w-xl">Shop the directory, redeem coupons, bid in the auction, and contribute to the Community & Youth Leadership funds — all in one app.</p>
            <div className="flex flex-wrap gap-3 mt-5">
              <Link to="/directory" className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium">Browse Directory</Link>
              <Link to="/auction" className="px-4 py-2.5 rounded-lg border border-green-600/40 text-green-400 hover:bg-green-600/10 text-sm font-medium">Enter Auction</Link>
            </div>
          </div>
          <div className="pbb-card p-5 w-full lg:w-80">
            <div className="text-gray-400 text-sm">Your points balance</div>
            <div className="text-4xl font-bold text-green-400 mt-1">{user?.points?.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">Tier: {user?.tier} • Member since {user?.memberSince}</div>
            <Link to="/loyalty" className="mt-4 inline-flex items-center gap-1 text-green-400 text-sm hover:underline">Redeem points <ChevronRight size={14} /></Link>
          </div>
        </div>
      </div>

      <FundraiserThermometer goalPts={100000} />

      <PBBLoyaltyCard />

      <ActivityTicker />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {to:'/auction', icon:Gavel, label:'Auction Store', sub:'Bid & win'},
          {to:'/coupons', icon:Ticket, label:'My Coupons', sub:'Save today'},
          {to:'/events', icon:Calendar, label:'Events', sub:'Buy tickets'},
          {to:'/leaderboard', icon:Trophy, label:'Leaderboard', sub:'Top earners'},
        ].map(t => (
          <Link key={t.to} to={t.to} className="pbb-card p-4 flex flex-col gap-2 hover:-translate-y-0.5">
            <t.icon className="text-green-400" size={22} />
            <div className="font-semibold text-sm">{t.label}</div>
            <div className="text-xs text-gray-400">{t.sub}</div>
          </Link>
        ))}
      </div>

      <div className="pbb-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 text-green-400"><TrendingUp size={18} /><span className="font-semibold">Community Impact • Live</span></div>
            <div className="text-2xl font-bold mt-1">${totalFund.toLocaleString()} <span className="text-gray-400 text-sm font-normal">/ ${funds.goal?.toLocaleString()} goal</span></div>
          </div>
          <Link to="/funds" className="text-sm text-green-400 hover:underline flex items-center gap-1">View details <ChevronRight size={14} /></Link>
        </div>
        <div className="h-3 rounded-full bg-[#111] overflow-hidden">
          <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-3">
            <div className="text-xs text-gray-400">Community Fund</div>
            <div className="text-lg font-semibold text-green-400">${funds.community?.toLocaleString()}</div>
          </div>
          <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-3">
            <div className="text-xs text-gray-400">Young Leadership Fund</div>
            <div className="text-lg font-semibold text-green-400">${funds.youth?.toLocaleString()}</div>
          </div>
        </div>
      </div>

      <section>
        <SectionHeader title="Featured Businesses" to="/directory" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {biz.map(b => (
            <Link to={`/business/${b.id}`} key={b.id} className="pbb-card overflow-hidden flex flex-col h-full">
              <div className="relative">
                <AppImage src={b.image} alt={b.name} rounded="none" />
                <div className="absolute -bottom-6 left-4 z-10">
                  <BusinessLogoAvatar business={b} size="md" />
                </div>
              </div>
              <div className="p-4 pt-8 flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{b.name}</div>
                  <div className="text-xs text-green-400">{b.rating} ★</div>
                </div>
                <div className="text-xs text-gray-400 mt-1">{b.category} • {b.city}</div>
                <div className="mt-3 inline-flex items-center gap-2 px-2 py-1 rounded bg-green-600/10 border border-green-600/30 text-green-400 text-xs"><Ticket size={12}/> {b.coupon}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <SectionHeader title="Upcoming Events" to="/events" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ev.map(e => (
            <Link to="/events" key={e.id} className="pbb-card overflow-hidden flex flex-col h-full">
              <AppImage src={e.image} alt={e.title} rounded="none" fallback={<Calendar size={24}/>} />
              <div className="p-4 flex flex-col flex-1">
                <div className="font-semibold">{e.title}</div>
                <div className="text-xs text-gray-400 mt-1">{e.date} • {e.city}</div>
                <div className="mt-2 text-sm text-green-400">{e.price === 0 ? 'Free' : `$${e.price}`}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="pbb-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-semibold"><Trophy size={16} className="text-green-400"/> Top Earners • Live</div>
            <Link to="/leaderboard" className="text-sm text-green-400 hover:underline">See all</Link>
          </div>
          <ul className="divide-y divide-[#1f1f1f]">
            {lb.map(l => (
              <li key={l.rank} className="flex items-center gap-3 py-2.5">
                <div className="w-6 text-gray-400 text-sm">#{l.rank}</div>
                <img src={l.avatar} alt="" className="w-8 h-8 rounded-full" />
                <div className="flex-1 text-sm">{l.name}</div>
                <div className="text-sm text-green-400 font-medium">{l.points.toLocaleString()} pts</div>
              </li>
            ))}
          </ul>
        </div>
        <div className="pbb-card p-5">
          <div className="flex items-center gap-2 font-semibold mb-3"><Users size={16} className="text-green-400"/> Popular in the Community</div>
          <div className="space-y-3 text-sm text-gray-300">
            <p>🌿 100% of Auction Store purchases fund the community.</p>
            <p>💚 Subscribers contribute $1/mo split between Community + Youth funds.</p>
            <p>✨ Earn 1000 points just for joining PBB.</p>
          </div>
          <Link to="/community" className="mt-4 inline-flex items-center gap-2 text-green-400 hover:underline text-sm"><Heart size={14}/> Join the conversation</Link>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ title, to }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      {to && <Link to={to} className="text-sm text-green-400 hover:underline flex items-center gap-1">View all <ChevronRight size={14}/></Link>}
    </div>
  );
}
